/**
 * Translations for the Beneficiary Secure Review page.
 *
 * We ship native translations for the highest-volume family-immigration
 * languages: English, Spanish, Chinese, Vietnamese, Tagalog, Korean, Hindi.
 * Other locales fall back to English with a friendly note.
 *
 * Keep keys stable — the component tree references them directly.
 */

export const BENEFICIARY_LOCALES = [
  { code: "en", name: "English",       nativeName: "English",       flag: "🇺🇸" },
  { code: "es", name: "Spanish",       nativeName: "Español",       flag: "🇲🇽" },
  { code: "zh", name: "Mandarin",      nativeName: "中文",           flag: "🇨🇳" },
  { code: "vi", name: "Vietnamese",    nativeName: "Tiếng Việt",    flag: "🇻🇳" },
  { code: "tl", name: "Tagalog",       nativeName: "Tagalog",       flag: "🇵🇭" },
  { code: "ko", name: "Korean",        nativeName: "한국어",         flag: "🇰🇷" },
  { code: "hi", name: "Hindi",         nativeName: "हिन्दी",         flag: "🇮🇳" },
];

const T = {
  // Page-level chrome
  secure_review_badge: {
    en: "Secure review", es: "Revisión segura", zh: "安全审查",
    vi: "Xem xét an toàn", tl: "Ligtas na review", ko: "안전한 검토", hi: "सुरक्षित समीक्षा",
  },
  hi_verify_title: {
    en: (name) => `Hi ${name} — let's verify it's you.`,
    es: (name) => `Hola ${name} — confirmemos que eres tú.`,
    zh: (name) => `你好 ${name} — 让我们确认这是您本人。`,
    vi: (name) => `Xin chào ${name} — hãy xác minh đây là bạn.`,
    tl: (name) => `Kumusta ${name} — patunayan natin na ikaw ito.`,
    ko: (name) => `안녕하세요 ${name}님 — 본인 확인을 진행하겠습니다.`,
    hi: (name) => `नमस्ते ${name} — यह पुष्टि करते हैं कि यह आप हैं।`,
  },
  otp_title: {
    en: "One more step.", es: "Un paso más.", zh: "还差一步。",
    vi: "Còn một bước nữa.", tl: "Isa pang hakbang.", ko: "한 단계 더 남았습니다.", hi: "एक और कदम।",
  },
  edit_title: {
    en: (name) => `Review your information, ${name}.`,
    es: (name) => `Revisa tu información, ${name}.`,
    zh: (name) => `请查看您的信息,${name}。`,
    vi: (name) => `Vui lòng xem lại thông tin của bạn, ${name}.`,
    tl: (name) => `Suriin ang iyong impormasyon, ${name}.`,
    ko: (name) => `${name}님의 정보를 확인해 주세요.`,
    hi: (name) => `अपनी जानकारी की समीक्षा करें, ${name}।`,
  },
  intro_dob: {
    en: (role) => `The ${role} on your family-based case invited you to review the data collected about you.`,
    es: (role) => `El ${role} de tu caso familiar te ha invitado a revisar la información recopilada sobre ti.`,
    zh: (role) => `您家庭移民案件中的${role}邀请您查看已收集的关于您的信息。`,
    vi: (role) => `${role} trong hồ sơ định cư diện gia đình của bạn đã mời bạn xem lại dữ liệu thu thập về bạn.`,
    tl: (role) => `Inanyayahan ka ng ${role} sa iyong family-based na kaso na suriin ang datos na naipon tungkol sa iyo.`,
    ko: (role) => `가족 기반 이민 케이스의 ${role}가 귀하에 대해 수집된 정보를 검토해 달라고 초대했습니다.`,
    hi: (role) => `आपके परिवार-आधारित मामले के ${role} ने आपसे आपके बारे में एकत्रित डेटा की समीक्षा करने का अनुरोध किया है।`,
  },
  intro_otp: {
    en: (email) => `We sent a 6-digit code to ${email}. Enter it below to unlock your review.`,
    es: (email) => `Enviamos un código de 6 dígitos a ${email}. Ingrésalo abajo para desbloquear tu revisión.`,
    zh: (email) => `我们已将一个 6 位数验证码发送到 ${email}。请在下方输入以进入您的资料。`,
    vi: (email) => `Chúng tôi đã gửi mã 6 chữ số đến ${email}. Nhập vào bên dưới để mở khóa hồ sơ.`,
    tl: (email) => `Nagpadala kami ng 6-digit na code sa ${email}. Ilagay ito sa ibaba upang buksan ang iyong review.`,
    ko: (email) => `${email}(으)로 6자리 코드를 보냈습니다. 아래에 입력하여 검토를 시작하세요.`,
    hi: (email) => `हमने ${email} पर 6-अंकों का कोड भेजा है। समीक्षा शुरू करने के लिए इसे नीचे दर्ज करें।`,
  },
  intro_edit: {
    en: "Correct anything that's wrong. Fill in what's missing. Add a note if you want. When you're done, click Submit.",
    es: "Corrige cualquier error. Completa lo que falta. Agrega una nota si lo deseas. Cuando termines, haz clic en Enviar.",
    zh: "修正任何错误的信息,补充缺失的部分。如果需要,可以添加备注。完成后,点击“提交”。",
    vi: "Sửa lại bất kỳ thông tin nào sai. Điền vào những gì còn thiếu. Thêm ghi chú nếu muốn. Khi xong, nhấn Gửi.",
    tl: "Ayusin ang anumang mali. Punan ang mga nawawala. Magdagdag ng tala kung gusto mo. Kapag tapos na, i-click ang Isumite.",
    ko: "잘못된 부분을 수정하고, 누락된 항목을 채우세요. 원한다면 메모를 추가할 수 있습니다. 완료되면 제출을 클릭하세요.",
    hi: "किसी भी गलत जानकारी को सुधारें। जो जानकारी छूट गई है उसे भरें। यदि चाहें तो नोट जोड़ें। जब हो जाए, तो सबमिट पर क्लिक करें।",
  },
  message_from_sender: {
    en: "Message from the sender", es: "Mensaje del remitente", zh: "发件人留言",
    vi: "Tin nhắn từ người gửi", tl: "Mensahe mula sa nagpadala", ko: "보낸 사람의 메시지", hi: "प्रेषक का संदेश",
  },
  label_dob: {
    en: "Your date of birth", es: "Tu fecha de nacimiento", zh: "您的出生日期",
    vi: "Ngày sinh của bạn", tl: "Ang iyong petsa ng kapanganakan", ko: "생년월일", hi: "आपकी जन्म तिथि",
  },
  hint_dob: {
    en: "We ask because the link alone isn't enough — your date of birth adds a second lock.",
    es: "Preguntamos porque el enlace por sí solo no es suficiente — tu fecha de nacimiento añade una segunda verificación.",
    zh: "我们询问是因为仅凭链接不够 — 出生日期作为第二重验证,能更好地保护您。",
    vi: "Chúng tôi hỏi vì chỉ đường link thì chưa đủ — ngày sinh của bạn là lớp bảo mật thứ hai.",
    tl: "Tinatanong namin dahil hindi sapat ang link lang — ang petsa ng kapanganakan ay pangalawang lock.",
    ko: "링크만으로는 부족하기 때문에, 생년월일을 두 번째 잠금 장치로 사용합니다.",
    hi: "हम इसलिए पूछते हैं क्योंकि केवल लिंक पर्याप्त नहीं है — आपकी जन्म तिथि दूसरा सुरक्षा स्तर जोड़ती है।",
  },
  btn_continue: { en: "Continue", es: "Continuar", zh: "继续", vi: "Tiếp tục", tl: "Magpatuloy", ko: "계속", hi: "जारी रखें" },
  btn_verify_code: { en: "Verify code", es: "Verificar código", zh: "验证代码", vi: "Xác minh mã", tl: "I-verify ang code", ko: "코드 확인", hi: "कोड सत्यापित करें" },
  btn_send_new_code: { en: "Send a new code", es: "Enviar un nuevo código", zh: "发送新代码", vi: "Gửi mã mới", tl: "Magpadala ng bagong code", ko: "새 코드 전송", hi: "नया कोड भेजें" },
  btn_save: { en: "Save", es: "Guardar", zh: "保存", vi: "Lưu", tl: "I-save", ko: "저장", hi: "सेव करें" },
  btn_submit_review: { en: "Submit review", es: "Enviar revisión", zh: "提交", vi: "Gửi bản xem lại", tl: "Isumite ang review", ko: "제출", hi: "समीक्षा सबमिट करें" },
  label_otp: {
    en: "Enter the 6-digit code", es: "Ingresa el código de 6 dígitos", zh: "输入 6 位验证码",
    vi: "Nhập mã 6 chữ số", tl: "Ilagay ang 6-digit na code", ko: "6자리 코드 입력", hi: "6-अंकों का कोड दर्ज करें",
  },
  hint_scope: {
    en: "You can only see and edit your own information. Nothing else about the case is visible.",
    es: "Solo puedes ver y editar tu propia información. No se muestra nada más del caso.",
    zh: "您只能查看和编辑您本人的信息。案件的其他内容对您不可见。",
    vi: "Bạn chỉ có thể xem và chỉnh sửa thông tin của chính mình. Không thấy được nội dung khác của hồ sơ.",
    tl: "Nakikita at ma-e-edit mo lang ang iyong sariling impormasyon. Walang ibang bahagi ng kaso ang makikita.",
    ko: "본인의 정보만 확인하고 수정할 수 있습니다. 케이스의 다른 부분은 표시되지 않습니다.",
    hi: "आप केवल अपनी जानकारी देख और संपादित कर सकते हैं। मामले का बाकी विवरण दिखाई नहीं देगा।",
  },
  submitted_title: { en: "Thank you!", es: "¡Gracias!", zh: "谢谢!", vi: "Cảm ơn bạn!", tl: "Salamat!", ko: "감사합니다!", hi: "धन्यवाद!" },
  submitted_body: {
    en: "Your review has been sent. You can close this tab.",
    es: "Se ha enviado tu revisión. Puedes cerrar esta pestaña.",
    zh: "您的审核已发送。您可以关闭此页面了。",
    vi: "Bản xem lại của bạn đã được gửi đi. Bạn có thể đóng tab này.",
    tl: "Naipadala na ang iyong review. Puwede mo nang isara ang tab na ito.",
    ko: "검토 내용이 전송되었습니다. 이 탭을 닫으셔도 됩니다.",
    hi: "आपकी समीक्षा भेज दी गई है। आप इस टैब को बंद कर सकते हैं।",
  },
  language: { en: "Language", es: "Idioma", zh: "语言", vi: "Ngôn ngữ", tl: "Wika", ko: "언어", hi: "भाषा" },
  upload_section_title: {
    en: "Upload documents", es: "Subir documentos", zh: "上传文件", vi: "Tải lên tài liệu",
    tl: "Mag-upload ng mga dokumento", ko: "문서 업로드", hi: "दस्तावेज़ अपलोड करें",
  },
  upload_section_body: {
    en: "Add your own docs (passport, birth cert, marriage/divorce cert, family registry). You can also add docs for your children, spouse, or parents already on the case.",
    es: "Agrega tus propios documentos (pasaporte, acta de nacimiento, acta de matrimonio/divorcio, registro familiar). También puedes agregar documentos de tus hijos, cónyuge o padres ya incluidos en el caso.",
    zh: "上传您自己的文件(护照、出生证明、结婚/离婚证书、家庭户籍等)。您也可以为案件中已列出的子女、配偶或父母上传文件。",
    vi: "Tải lên các giấy tờ của bạn (hộ chiếu, giấy khai sinh, giấy chứng nhận kết hôn/ly hôn, sổ hộ khẩu). Bạn cũng có thể tải lên giấy tờ cho con, vợ/chồng hoặc cha mẹ đã có trong hồ sơ.",
    tl: "Idagdag ang iyong sariling mga dokumento (passport, birth cert, marriage/divorce cert, family registry). Puwede ka ring magdagdag ng mga dokumento para sa iyong mga anak, asawa, o mga magulang na nasa kaso na.",
    ko: "본인의 서류(여권, 출생증명서, 결혼/이혼증명서, 가족관계증명서 등)를 업로드하세요. 케이스에 이미 등록된 자녀, 배우자, 부모의 서류도 추가할 수 있습니다.",
    hi: "अपने दस्तावेज़ जोड़ें (पासपोर्ट, जन्म प्रमाण पत्र, विवाह/तलाक प्रमाण पत्र, पारिवारिक रजिस्ट्री)। आप मामले में पहले से मौजूद अपने बच्चों, जीवनसाथी, या माता-पिता के लिए भी दस्तावेज़ जोड़ सकते हैं।",
  },
  upload_who: {
    en: "Whose document is this?", es: "¿De quién es este documento?", zh: "这是谁的文件?",
    vi: "Đây là giấy tờ của ai?", tl: "Kanino ang dokumentong ito?", ko: "누구의 문서인가요?",
    hi: "यह दस्तावेज़ किसका है?",
  },
};

/** Return the translated string (or English fallback if the locale isn't defined). */
export function bt(key, locale, ...args) {
  const entry = T[key];
  if (!entry) return key;
  const value = entry[locale] || entry.en || key;
  if (typeof value === "function") return value(...args);
  return value;
}
